/**
 * Escarpment Labs (Guelph, Ontario) — Canadian liquid cultures, including the
 * Le Labo strains now sold under the same roof.
 *
 * Temperatures are °C, converted from the °F ranges the lab publishes and
 * rounded to the nearest whole degree. Escarpment prints alcohol tolerance as
 * a word (Medium, Medium-High) rather than a figure, so that field is `null`
 * rather than a guessed number. STA1 is taken from the lab's diastatic flag
 * where published.
 */

import type { Yeast } from '../../schema/yeast';

/** Escarpment Labs cultures. @see Yeast */
export const escarpmentYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // Core ales
  // -------------------------------------------------------------------------
  {
    id: 'escarpment-vermont-ale',
    lab: 'Escarpment Labs',
    productCode: 'VERMONT',
    name: 'Vermont Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "Escarpment's take on the New England ale strain: peach and apricot esters, a rounded mouthfeel and enough haze stability to carry a heavy dry hop. Ferment it warm to push the fruit, cooler if you want it closer to a clean pale ale.",
    tags: ['hazy', 'new-england', 'ipa', 'stone-fruit', 'esters'],
    url: 'https://escarpmentlabs.com/products/vermont-ale',
    provenance: 'representative',
  },
  {
    id: 'escarpment-foggy-london-ale',
    lab: 'Escarpment Labs',
    productCode: 'FOGGY-LONDON',
    name: 'Foggy London Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 75 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'An English ale strain kept for its low attenuation and soft, fruity finish — useful in hazy beers where you want body from the yeast rather than from oats alone. Also a straightforward choice for bitters and milds.',
    tags: ['english', 'hazy', 'bitter', 'esters', 'full-body'],
    url: 'https://escarpmentlabs.com/products/foggy-london-ale',
    provenance: 'representative',
  },
  {
    id: 'escarpment-cali-ale',
    lab: 'Escarpment Labs',
    productCode: 'CALI',
    name: 'Cali Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'The standard clean American ale strain — neutral, reliable and happy across a wide temperature band. Use it whenever the recipe should taste of malt and hops and nothing else.',
    tags: ['clean', 'neutral', 'american', 'pale-ale', 'ipa', 'west-coast'],
    url: 'https://escarpmentlabs.com/products/cali-ale',
    provenance: 'representative',
  },
  {
    id: 'escarpment-hydra',
    lab: 'Escarpment Labs',
    productCode: 'HYDRA',
    name: 'Hydra',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A hybrid of Escarpment Cerberus and Vermont, bred for hazy IPA that needs to stay juicy after hop creep. Low attenuation and mango-citrus esters keep a DIPA from drying out; it wants extra oxygen at pitch or it can stall.',
    tags: ['hazy', 'ipa', 'hybrid', 'mango', 'low-attenuation', 'new-england'],
    url: 'https://escarpmentlabs.com/products/hydra',
    provenance: 'spec-sheet',
  },
  {
    id: 'escarpment-superfog',
    lab: 'Escarpment Labs',
    productCode: 'SUPERFOG',
    name: 'Superfog',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 84 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Foggy London evolved for thiol release and haze: passionfruit and grapefruit lift on top of the usual stone fruit, with extra glycerol so the beer stays full. Ferment on the cool side of the range if you want the thiols to lead.',
    tags: ['hazy', 'thiol', 'ipa', 'biotransformation', 'passionfruit', 'english'],
    url: 'https://escarpmentlabs.com/products/superfog',
    provenance: 'spec-sheet',
  },
  {
    id: 'escarpment-thiol-libre',
    lab: 'Escarpment Labs',
    productCode: 'THIOL-LIBRE',
    name: 'Thiol Libre',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A high thiol-releasing ale strain: it liberates bound aroma compounds from malt and hops into passionfruit and grapefruit notes, giving hop character the hops alone will not produce. Best with thiol-rich varieties and a generous whirlpool or biotransformation dry hop.',
    tags: ['thiol', 'biotransformation', 'hazy', 'ipa', 'tropical', 'passionfruit'],
    url: 'https://escarpmentlabs.com/products/thiol-libre',
    provenance: 'representative',
  },
  {
    id: 'escarpment-kolsch-ale',
    lab: 'Escarpment Labs',
    productCode: 'KOLSCH',
    name: 'Kölsch Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 13, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A top-cropping kölsch hybrid that ferments clean and slightly winey at ale-cool temperatures. Cold condition it after terminal gravity — the beer is only half-finished when primary ends.',
    tags: ['kolsch', 'hybrid', 'clean', 'german', 'crisp'],
    url: 'https://escarpmentlabs.com/products/kolsch',
    provenance: 'representative',
  },
  {
    id: 'escarpment-classic-wit',
    lab: 'Escarpment Labs',
    productCode: 'CLASSIC-WIT',
    name: 'Classic Wit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Belgian wheat-beer strain producing clove-forward phenols with a lighter banana note than a hefeweizen yeast. Stays in suspension, so the beer keeps the cloudy pour a wit is supposed to have.',
    tags: ['witbier', 'belgian', 'wheat', 'phenolic', 'spicy'],
    url: 'https://escarpmentlabs.com/products/classic-witbier',
    provenance: 'representative',
  },
  {
    id: 'escarpment-fruity-wit',
    lab: 'Escarpment Labs',
    productCode: 'FRUITY-WIT',
    name: 'Fruity Wit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 78 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A wit strain that leans orange-peel and stone fruit rather than clove, so coriander and citrus additions have somewhere to sit. Useful when a wheat beer should taste of fruit first and spice second.',
    tags: ['witbier', 'belgian', 'wheat', 'esters', 'citrus'],
    url: 'https://escarpmentlabs.com/products/fruity-witbier',
    provenance: 'representative',
  },
  {
    id: 'escarpment-weizen-i',
    lab: 'Escarpment Labs',
    productCode: 'WEIZEN-I',
    name: 'Weizen I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 17, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Bavarian weizen culture balancing banana ester against clove phenol. Pitch cool and heavy for clove, warm and thin for banana; either way it stays in the glass rather than dropping out.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'phenolic', 'german'],
    url: 'https://escarpmentlabs.com/products/weizen-i',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Farmhouse, saison and mixed
  // -------------------------------------------------------------------------
  {
    id: 'escarpment-saison-blend',
    lab: 'Escarpment Labs',
    productCode: 'SAISON-BLEND',
    name: 'Saison Blend',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'Several saison strains pitched together so the fast, phenolic yeast leads and a more attenuative partner finishes the job — the practical answer to the classic saison stall. Expect pepper, lemon zest and a very dry beer.',
    tags: ['saison', 'blend', 'farmhouse', 'phenolic', 'dry-finish', 'belgian'],
    provenance: 'representative',
  },
  {
    id: 'escarpment-french-saison',
    lab: 'Escarpment Labs',
    productCode: 'FRENCH-SAISON',
    name: 'French Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 20, max: 30 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: true,
    description:
      'A Dupont-adjacent saison that finishes relentlessly dry with pepper and citrus rather than stalling mid-gravity. Diastatic, so treat shared lines as contaminated and do not package until gravity has actually stopped.',
    tags: ['saison', 'farmhouse', 'phenolic', 'diastatic', 'dry-finish', 'french'],
    url: 'https://escarpmentlabs.com/products/french-saison',
    provenance: 'representative',
  },
  {
    id: 'escarpment-saison-maison',
    lab: 'Escarpment Labs',
    productCode: 'SAISON-MAISON',
    name: 'Saison Maison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 78, max: 88 },
    temperatureC: { min: 20, max: 28 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      "Escarpment's house farmhouse culture: peppery, lightly citric and more willing to finish than a classic Dupont pitch. A good everyday saison yeast when you want character without a three-week stall.",
    tags: ['saison', 'farmhouse', 'phenolic', 'house', 'belgian'],
    url: 'https://escarpmentlabs.com/products/saison-maison',
    provenance: 'representative',
  },
  {
    id: 'escarpment-lactobacillus-blend-2',
    lab: 'Escarpment Labs',
    productCode: 'LACTO-2',
    name: 'Lactobacillus Blend 2.0',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 30, max: 40 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A kettle-souring lactobacillus blend that drops wort pH into the 3.2–3.5 region in a day when held hot. It produces lactic acid, not alcohol — pitch a Saccharomyces afterwards to ferment the beer. The attenuation figure is nominal.',
    tags: ['sour', 'lactobacillus', 'kettle-sour', 'gose', 'berliner-weisse'],
    url: 'https://escarpmentlabs.com/products/lactobacillus-blend-2',
    provenance: 'representative',
  },
  {
    id: 'escarpment-berliner-brett-blend',
    lab: 'Escarpment Labs',
    productCode: 'BERLINER-BRETT',
    name: 'Berliner Brett Blend',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 75, max: 90 },
    temperatureC: { min: 18, max: 28 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A mixed Brettanomyces culture aimed at Berliner-style tart wheat beers and mixed-fermentation sours. Funk builds over weeks rather than days; package only when gravity and acidity have both stopped moving.',
    tags: ['brett', 'wild', 'berliner-weisse', 'sour', 'mixed-fermentation'],
    url: 'https://escarpmentlabs.com/products/berliner-brett-blend',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Kveik
  // -------------------------------------------------------------------------
  {
    id: 'escarpment-kveik-blend',
    lab: 'Escarpment Labs',
    productCode: 'KVEIK-BLEND',
    name: 'Kveik Blend',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 25, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A blend of Norwegian farmhouse cultures selected to ferment fast and hot while keeping a mixed tropical and citrus ester profile no single strain gives. Flocculates hard once it is finished, which makes it easy to harvest and repitch.',
    tags: ['kveik', 'norwegian', 'blend', 'fast', 'tropical', 'farmhouse'],
    provenance: 'representative',
  },
  {
    id: 'escarpment-krispy',
    lab: 'Escarpment Labs',
    productCode: 'KRISPY',
    name: 'KRISPY',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 15, max: 32 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      "A lab-evolved kveik that ferments clean and lager-like at ale temperatures, finishing drier and brighter than a farmhouse culture. The lab's clean-lager advice is a cool start ramping toward 22 °C; it will still ferment hotter, with more ester, because it is kveik.",
    tags: ['kveik', 'clean', 'pseudo-lager', 'fast', 'crisp', 'norwegian'],
    url: 'https://escarpmentlabs.com/products/krispy',
    provenance: 'representative',
  },
  {
    id: 'escarpment-voss-kveik',
    lab: 'Escarpment Labs',
    productCode: 'VOSS',
    name: 'Voss Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 40 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'The Sigmund Gjernes Voss culture: orange-citrus esters that intensify as you push the temperature, with a finish in two or three days at 35 °C. Harvests easily after it drops, which is why it became the default kveik.',
    tags: ['kveik', 'voss', 'orange', 'norwegian', 'fast', 'farmhouse'],
    url: 'https://escarpmentlabs.com/products/voss-kveik',
    provenance: 'representative',
  },
  {
    id: 'escarpment-hornindal-kveik',
    lab: 'Escarpment Labs',
    productCode: 'HORNINDAL',
    name: 'Hornindal Kveik Blend',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Hornindal farmhouse blend throwing pineapple, mango and stone fruit rather than Voss orange. Ferment it hot under a heavy dry hop for a tropical IPA without temperature control.',
    tags: ['kveik', 'hornindal', 'tropical', 'norwegian', 'fast', 'ipa'],
    url: 'https://escarpmentlabs.com/products/hornindal-kveik-blend',
    provenance: 'representative',
  },
  {
    id: 'escarpment-ebbegarden-kveik',
    lab: 'Escarpment Labs',
    productCode: 'EBBEGARDEN',
    name: 'Ebbegarden Kveik Blend',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Stordal farmhouse kveik known for apple, pear and tropical fruit — a hop-friendly profile that suits pale ales and hazies as much as traditional farmhouse ales. Fast at the hot end of the range, cleaner if you hold it down.',
    tags: ['kveik', 'ebbegarden', 'apple', 'tropical', 'norwegian', 'ipa'],
    url: 'https://escarpmentlabs.com/products/ebbegarden-kveik-blend',
    provenance: 'representative',
  },
  {
    id: 'escarpment-laerdal-kveik',
    lab: 'Escarpment Labs',
    productCode: 'LAERDAL',
    name: 'Lærdal Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 38 },
    flocculation: 'high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Lærdal farmhouse isolate with a gentler fruit ester than Hornindal and a clean enough cool-end profile to pass as a pale ale yeast. Another option when you want kveik speed without a loud orange note.',
    tags: ['kveik', 'laerdal', 'norwegian', 'fast', 'farmhouse', 'clean'],
    url: 'https://escarpmentlabs.com/products/laerdal-kveik',
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Lagers
  // -------------------------------------------------------------------------
  {
    id: 'escarpment-lager-blend',
    lab: 'Escarpment Labs',
    productCode: 'LAGER-BLEND',
    name: 'Lager Blend',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A blend of lager strains chosen to ferment faster and cleaner than any one of them alone, with a shorter diacetyl rest than a traditional single-strain pitch needs. Works well for helles, pilsner and dark lagers on a compressed schedule.',
    tags: ['lager', 'blend', 'clean', 'helles', 'pilsner', 'fast'],
    provenance: 'representative',
  },
  {
    id: 'escarpment-isar-lager',
    lab: 'Escarpment Labs',
    productCode: 'ISAR',
    name: 'Isar Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 82 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Munich-lineage workhorse: clean, well-flocculating and low in diacetyl, equally at home in helles, märzen and bock. The default Escarpment lager when you want German malt to read without sulfur hanging around.',
    tags: ['lager', 'german', 'helles', 'bock', 'clean', 'munich'],
    url: 'https://escarpmentlabs.com/products/isar-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'escarpment-superlager',
    lab: 'Escarpment Labs',
    productCode: 'SUPERLAGER',
    name: 'Superlager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 82 },
    temperatureC: { min: 12, max: 16 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'Isar Lager with thiol biotransformation switched on: the same clean lager chassis, plus passionfruit and white-wine notes when you feed it thiol-rich hops. Built for cold IPA and hop-forward lagers; pull the hops back if you want a classic helles.',
    tags: ['lager', 'thiol', 'cold-ipa', 'hop-forward', 'clean', 'german'],
    url: 'https://escarpmentlabs.com/products/superlager',
    provenance: 'spec-sheet',
  },
  {
    id: 'escarpment-biergarten-lager',
    lab: 'Escarpment Labs',
    productCode: 'BIERGARTEN',
    name: 'Biergarten Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A malty Bavarian lager strain aimed at festbier, märzen and helles that should taste of bread crust rather than of the yeast. Give it a proper diacetyl rest; it is not the fastest lager in the catalog.',
    tags: ['lager', 'german', 'marzen', 'festbier', 'malty', 'helles'],
    url: 'https://escarpmentlabs.com/products/biergarten-lager',
    provenance: 'representative',
  },
  {
    id: 'escarpment-premium-pils',
    lab: 'Escarpment Labs',
    productCode: 'PREMIUM-PILS',
    name: 'Premium Pils',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 9, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: null,
    sta1: false,
    description:
      'A Czech-leaning pilsner strain that finishes dry and snappy with a faint malt sweetness left behind. Slower than a German 34/70 type; the extra tank time is what makes a Bohemian pilsner taste like one.',
    tags: ['lager', 'czech', 'pilsner', 'crisp', 'dry'],
    url: 'https://escarpmentlabs.com/products/premium-pilsner',
    provenance: 'representative',
  },
];
